/**
 * The product editor's form values and their conversion from / to the API.
 * Field paths match the API's error paths ("variants.1.price", "attributes.voltage"), so server errors land on the
 * right input (`applyServerErrorPaths`).
 */

import type {
  ConditionEnum,
  FitmentTypeEnum,
  FormSchema,
  ProductDetail,
  ProductWriteRequest,
} from "@motolynk/api-client";

export interface VariantValues {
  uid: string | null;
  options: Record<string, string>;
  sku: string;
  price: string;
  mrp: string;
  cost_price: string;
  stock: string;
  is_default: boolean;
  is_active: boolean;
}

export interface MediaValues {
  media: string;
  url: string | null;
  name: string;
  is_primary: boolean;
}

export interface FitmentValues {
  make: string;
  make_name: string;
  model: string;
  model_name: string;
  variant: string;
  variant_name: string;
  year_from: string;
  year_to: string;
  note: string;
}

export interface FaqValues {
  /** The saved row's uid (null for a question added in this session). */
  uid: string | null;
  question: string;
  answer: string;
  is_published: boolean;
}

export type Returnable = "default" | "yes" | "no";

export interface EditorValues {
  title: string;
  slug: string;
  category: string;
  brand: string;
  condition: ConditionEnum;
  mpn: string;
  oem_number: string;
  short_description: string;
  description: string;
  fitment_type: FitmentTypeEnum;
  tax_class: string;
  package_weight_g: string;
  package_length_mm: string;
  package_width_mm: string;
  package_height_mm: string;
  is_hazmat: boolean;
  hazmat_info: string;
  warranty_months: string;
  is_returnable: Returnable;
  is_published: boolean;
  seo_title: string;
  seo_description: string;
  /** Hindi / Marathi / Gujarati texts (P6-13) */
  translations: Record<string, Record<string, string>>;
  attributes: Record<string, unknown>;
  variants: VariantValues[];
  media: MediaValues[];
  fitment: FitmentValues[];
  faqs: FaqValues[];
}

/** Where the editor reads its choices from (the admin and the vendor panel use different endpoints). */
export interface EditorSource {
  /** Distinguishes cached queries per panel. */
  key: string;
  categories: () => Promise<CategoryNode[]>;
  brands: () => Promise<{ uid: string; name: string }[]>;
  taxClasses: () => Promise<{ uid: string; name: string }[]>;
  formSchema: (categoryUid: string) => Promise<FormSchema>;
}

export interface CategoryNode {
  uid: string;
  name: string;
  children: CategoryNode[];
}

const text = (value: unknown) => (value === null || value === undefined ? "" : String(value));

/** A decimal the API sent in exponent form ("1.2E+2", from Decimal.normalize()) as plain digits ("120"). */
const plainNumber = (value: unknown) =>
  typeof value === "string" && /^-?\d+(\.\d+)?e[+-]?\d+$/i.test(value.trim())
    ? String(Number(value))
    : value;

export function newVariant(options: Record<string, string> = {}, isDefault = false): VariantValues {
  return {
    uid: null,
    options,
    sku: "",
    price: "",
    mrp: "",
    cost_price: "",
    stock: "0",
    is_default: isDefault,
    is_active: true,
  };
}

export function emptyValues(): EditorValues {
  return {
    title: "",
    slug: "",
    category: "",
    brand: "",
    condition: "new",
    mpn: "",
    oem_number: "",
    short_description: "",
    description: "",
    fitment_type: "universal",
    tax_class: "",
    package_weight_g: "",
    package_length_mm: "",
    package_width_mm: "",
    package_height_mm: "",
    is_hazmat: false,
    hazmat_info: "",
    warranty_months: "",
    is_returnable: "default",
    is_published: true,
    seo_title: "",
    seo_description: "",
    translations: {},
    attributes: {},
    variants: [newVariant({}, true)],
    media: [],
    fitment: [],
    faqs: [],
  };
}

export function toValues(product: ProductDetail): EditorValues {
  return {
    title: product.title,
    slug: product.slug,
    category: product.category.uid,
    brand: product.brand?.uid ?? "",
    condition: product.condition,
    mpn: product.mpn,
    oem_number: product.oem_number,
    short_description: product.short_description,
    description: product.description,
    fitment_type: product.fitment_type,
    tax_class: product.tax_class?.uid ?? "",
    package_weight_g: text(product.package_weight_g || ""),
    package_length_mm: text(product.package_length_mm || ""),
    package_width_mm: text(product.package_width_mm || ""),
    package_height_mm: text(product.package_height_mm || ""),
    is_hazmat: product.is_hazmat,
    hazmat_info: product.hazmat_info,
    warranty_months: text(product.warranty_months),
    is_returnable:
      product.is_returnable === null ? "default" : product.is_returnable ? "yes" : "no",
    is_published: product.is_published,
    seo_title: product.seo_title,
    seo_description: product.seo_description,
    translations: product.translations ?? {},
    attributes: Object.fromEntries(
      Object.entries(product.attributes ?? {}).map(([code, value]) => [code, plainNumber(value)]),
    ),
    variants: product.variants.map((variant) => ({
      uid: variant.uid,
      options: { ...variant.options },
      sku: variant.sku,
      price: text(variant.price),
      mrp: text(variant.mrp),
      cost_price: text(variant.cost_price),
      stock: text(variant.stock),
      is_default: variant.is_default,
      is_active: variant.is_active,
    })),
    media: product.media.map((row) => ({
      media: row.media,
      url: row.url,
      name: row.name,
      is_primary: row.is_primary,
    })),
    fitment: product.fitment.map((row) => ({
      make: row.make.uid,
      make_name: row.make.name,
      model: row.model?.uid ?? "",
      model_name: row.model?.name ?? "",
      variant: row.variant?.uid ?? "",
      variant_name: row.variant?.name ?? "",
      year_from: text(row.year_from),
      year_to: text(row.year_to),
      note: row.note,
    })),
    faqs: (product.faqs ?? []).map((faq) => ({
      uid: faq.uid,
      question: faq.question,
      answer: faq.answer,
      is_published: faq.is_published,
    })),
  };
}

/**
 * A whole-number field for the API: empty → null ("not set"). The inputs only accept digits and the form's rules
 * refuse anything else before saving, so the number is sent as typed (never rounded or cut down here).
 */
const whole = (value: string): number | null => {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : null;
};

/** The API payload; specifications of fields that aren't in the current schema are left out. */
export function toPayload(values: EditorValues, specCodes: string[]): ProductWriteRequest {
  const attributes: Record<string, unknown> = {};
  for (const code of specCodes) {
    const value = values.attributes[code];
    if (value === undefined || value === "" || (Array.isArray(value) && value.length === 0))
      continue;
    attributes[code] = value;
  }
  return {
    title: values.title,
    ...(values.slug.trim() ? { slug: values.slug.trim() } : {}),
    category: values.category || null,
    brand: values.brand || null,
    condition: values.condition,
    mpn: values.mpn,
    oem_number: values.oem_number,
    short_description: values.short_description,
    description: values.description,
    fitment_type: values.fitment_type,
    tax_class: values.tax_class || null,
    package_weight_g: whole(values.package_weight_g),
    package_length_mm: whole(values.package_length_mm),
    package_width_mm: whole(values.package_width_mm),
    package_height_mm: whole(values.package_height_mm),
    is_hazmat: values.is_hazmat,
    hazmat_info: values.hazmat_info,
    warranty_months: whole(values.warranty_months),
    is_returnable: values.is_returnable === "default" ? null : values.is_returnable === "yes",
    is_published: values.is_published,
    seo_title: values.seo_title,
    seo_description: values.seo_description,
    translations: values.translations,
    attributes,
    variants: values.variants.map((variant) => ({
      uid: variant.uid,
      options: variant.options,
      sku: variant.sku,
      price: variant.price.trim() || null,
      mrp: variant.mrp.trim() || null,
      cost_price: variant.cost_price.trim() || null,
      stock: whole(variant.stock),
      is_default: variant.is_default,
      is_active: variant.is_active,
    })),
    media: values.media.map((row) => ({ media: row.media, is_primary: row.is_primary })),
    fitment:
      values.fitment_type === "specific"
        ? values.fitment.map((row) => ({
            make: row.make,
            model: row.model || null,
            variant: row.variant || null,
            year_from: whole(row.year_from),
            year_to: whole(row.year_to),
            note: row.note,
          }))
        : [],
    faqs: values.faqs.map((faq) => ({
      uid: faq.uid,
      question: faq.question,
      answer: faq.answer,
      is_published: faq.is_published,
    })),
  };
}

export function newFaq(): FaqValues {
  return { uid: null, question: "", answer: "", is_published: true };
}

/** Every combination of the chosen options per axis (Cartesian product), in axis order. */
export function combinations(
  chosen: Record<string, string[]>,
  axes: string[],
): Record<string, string>[] {
  return axes
    .reduce<Record<string, string>[]>(
      (rows, axis) =>
        (chosen[axis] ?? []).flatMap((option) => rows.map((row) => ({ ...row, [axis]: option }))),
      [{}],
    )
    .filter((row) => axes.every((axis) => row[axis]));
}

export function sameOptions(
  a: Record<string, string>,
  b: Record<string, string>,
  axes: string[],
): boolean {
  return axes.every((axis) => a[axis] === b[axis]);
}

export function flattenCategories(
  nodes: CategoryNode[],
  path: string[] = [],
): { value: string; label: string }[] {
  return nodes.flatMap((node) => {
    const label = [...path, node.name];
    return [
      { value: node.uid, label: label.join(" > ") },
      ...flattenCategories(node.children ?? [], label),
    ];
  });
}
